import React, { useState, useEffect, useMemo } from 'react';
import {
  UsersIcon,
  UserPlusIcon,
  ArrowUpCircleIcon,
  ArrowDownCircleIcon,
  HeartIcon,
  BookOpenIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  SparklesIcon,
  UserGroupIcon,
  AcademicCapIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon as ArrowTrendingUpIconSolid,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import {
  StarIcon as StarIconSolid,
  UserIcon as UserIconSolid
} from '@heroicons/react/24/solid';

interface StatCardProps {
  icon: React.ElementType;
  title: string;
  value: string | number;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value, color, trend, subtitle }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 1000);
    return () => clearTimeout(timer);
  }, [value]);

  const getTrendIcon = () => {
    if (!trend) return null;
    return trend.isPositive ?
      <ArrowTrendingUpIconSolid className="h-4 w-4 text-green-500" /> :
      <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-between group relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 배경 패턴 */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent transform rotate-45 group-hover:scale-110 transition-transform duration-500"></div>
      </div>
      
      <div className="flex items-center space-x-4 relative z-10">
        <div className={`p-4 rounded-2xl mr-4 transform transition-all duration-300 ${color} relative overflow-hidden group-hover:scale-110`}>
          <Icon className="h-8 w-8 text-white relative z-10" />
          <div className="absolute inset-0 bg-white opacity-20 rounded-2xl transform scale-0 group-hover:scale-100 transition-transform duration-500"></div>
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{title}</p>
          <p className={`text-lg font-bold text-gray-800 dark:text-gray-200 transition-all duration-500 ${isAnimating ? 'scale-110' : 'scale-100'}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subtitle}</p>
          )}
          {trend && (
            <div className={`flex items-center mt-2 text-sm font-medium ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {getTrendIcon()}
              <span className="ml-1">{Math.abs(trend.value)}%</span>
            </div>
          )}
        </div>
      </div>
      
      <div className={`absolute top-4 right-4 transition-all duration-300 ${isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'}`}>
        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

interface StatsCardGridProps {
  totalMembers: number;
  activeNewcomers: number;
  weeklyIncome: number;
  weeklyExpense: number;
  monthlyGrowth?: number;
  newcomerGrowth?: number;
  netIncome?: number;
  profitMargin?: string;
  attendanceRate?: number;
  activeEvents?: number;
}

export const StatsCardGrid: React.FC<StatsCardGridProps> = ({
  totalMembers,
  activeNewcomers,
  weeklyIncome,
  weeklyExpense,
  monthlyGrowth = 0,
  newcomerGrowth = 0,
  netIncome = 0,
  profitMargin = '0',
  attendanceRate = 0,
  activeEvents = 0,
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const getProfitMarginColor = (margin: string) => {
    const numMargin = parseFloat(margin);
    if (numMargin >= 20) return 'text-green-600';
    if (numMargin >= 10) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {/* 총 등록 교인 */}
      <StatCard
        icon={UsersIcon}
        title="총 등록 교인"
        value={totalMembers.toLocaleString()}
        color="bg-gradient-to-br from-blue-500 to-blue-600"
        trend={{ value: monthlyGrowth, isPositive: monthlyGrowth >= 0 }}
        subtitle="전월 대비"
      />
      
      {/* 활동중인 새가족 */}
      <StatCard
        icon={UserPlusIcon}
        title="새가족 현황"
        value={activeNewcomers.toLocaleString()}
        color="bg-gradient-to-br from-green-500 to-emerald-600"
        trend={{ value: newcomerGrowth, isPositive: newcomerGrowth >= 0 }}
        subtitle="신규 가족"
      />
      
      {/* 금주 총수입 */}
      <StatCard
        icon={ArrowUpCircleIcon}
        title="금주 총수입"
        value={`${formatCurrency(weeklyIncome)}`}
        color="bg-gradient-to-br from-yellow-500 to-orange-500"
        subtitle="헌금 및 기부"
      />
      
      {/* 금주 총지출 */}
      <StatCard
        icon={ArrowDownCircleIcon}
        title="금주 총지출"
        value={`${formatCurrency(weeklyExpense)}`}
        color="bg-gradient-to-br from-red-500 to-pink-500"
        subtitle="운영 및 사역비"
      />

      {/* 순수익 */}
      <StatCard
        icon={ArrowTrendingUpIconSolid}
        title="주간 총계"
        value={`${formatCurrency(netIncome)}`}
        color="bg-gradient-to-br from-purple-500 to-indigo-600"
        subtitle="수입 - 지출"
      />

      {/* 헌금증감률 */}
      <StatCard
        icon={ChartBarIcon}
        title="헌금증감률"
        value={`${profitMargin}%`}
        color="bg-gradient-to-br from-teal-500 to-cyan-600"
        subtitle="수입지출대비"
      />

      {/* 출석률 */}
      <StatCard
        icon={CalendarIcon}
        title="주간 출석률"
        value={`${attendanceRate}%`}
        color="bg-gradient-to-br from-emerald-500 to-green-600"
        subtitle="평균 참여도"
      />

      {/* 활동 이벤트 */}
      <StatCard
        icon={CalendarIcon}
        title="활동 이벤트"
        value={activeEvents.toString()}
        color="bg-gradient-to-br from-orange-500 to-red-600"
        subtitle="이번주 활동"
      />
    </div>
  );
};

// 새가족 현황 섹션 - 강화된 디자인
export const NewcomerStatusSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'recent' | 'progress'>('overview');
  const [animatedValues, setAnimatedValues] = useState({
    totalNewcomers: 0,
    activeNewcomers: 0,
    adaptationRate: 0,
    ministryParticipation: 0
  });
  
  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedValues({
        totalNewcomers: 87,
        activeNewcomers: 72,
        adaptationRate: 83,
        ministryParticipation: 52
      });
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 60) return 'bg-blue-500';
    if (percentage >= 40) return 'bg-purple-500';
    return 'bg-orange-500';
  };
  
  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-purple-200 dark:border-purple-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl shadow-lg">
            <UserPlusIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">새가족 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">새로운 가족들의 성장 여정</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2 inline-block animate-pulse"></span>
            활발한 성장 중
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex space-x-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('recent')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'recent'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          최신 가족
        </button>
        <button
          onClick={() => setActiveTab('progress')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'progress'
              ? 'bg-purple-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          성장 과정
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 통계 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">이번월 신규 가족</p>
                <p className="text-lg font-bold text-purple-600 dark:text-purple-400 animate-pulse">+12</p>
              </div>
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">평균 적응 기간</p>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">3.2주</p>
              </div>
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <ClockIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-2">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">새가족 통계</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">전체 새가족 수</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{animatedValues.totalNewcomers}명</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">활동 중인 가족</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">{animatedValues.activeNewcomers}명 ({animatedValues.adaptationRate}%)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">사역 참여 가족</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">{Math.floor(animatedValues.totalNewcomers * animatedValues.ministryParticipation / 100)}명 ({animatedValues.ministryParticipation}%)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">완전 적응 가족</span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">{Math.floor(animatedValues.totalNewcomers * 0.44)}명 (44%)</span>
                </div>
              </div>
              
              {/* 실시간 상태 표시 */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">실시간 데이터 업데이트 중</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">방금 전</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recent' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">최신 등록 가족</h3>
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center shadow-md">
                      <UserIconSolid className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 dark:text-white">김가족 {item}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{item === 1 ? '3일 전' : item === 2 ? '5일 전' : '1주일 전'} 등록</p>
                    </div>
                    <div className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs">
                      활동 중
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 text-center">
                <button className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium">
                  더보기 →
                </button>
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">성장 과정</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">1단계: 환영</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">100%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className={`${getProgressColor(100)} h-3 rounded-full transition-all duration-1000`} style={{ width: '100%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">2단계: 적응</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">78%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className={`${getProgressColor(78)} h-3 rounded-full transition-all duration-1000`} style={{ width: '78%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">3단계: 사역 참여</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">52%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className={`${getProgressColor(52)} h-3 rounded-full transition-all duration-1000`} style={{ width: '52%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">4단계: 리더십</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">15%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div className={`${getProgressColor(15)} h-3 rounded-full transition-all duration-1000`} style={{ width: '15%' }}></div>
                  </div>
                </div>
              </div>
              
              {/* 성장 팁 */}
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-300">성장 팁</p>
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">새가족들이 3단계로 진입할 수 있도록 멘토링 프로그램을 강화하세요.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 양육관리 현황 섹션 - 멋진 디자인
export const DiscipleshipManagementSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'mentors' | 'progress'>('overview');
  
  return (
    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-2xl p-6 mb-8 shadow-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl">
            <AcademicCapIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">양육관리 현황</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">가족별 양육 과정 관리</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm font-medium">
            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 inline-block"></span>
            양육 활발
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="flex space-x-1 mb-6 bg-white dark:bg-gray-800 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'overview'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          전체 현황
        </button>
        <button
          onClick={() => setActiveTab('mentors')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'mentors'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          멘토 현황
        </button>
        <button
          onClick={() => setActiveTab('progress')}
          className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
            activeTab === 'progress'
              ? 'bg-blue-500 text-white shadow-md'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          진행 상황
        </button>
      </div>

      {/* 콘텐츠 영역 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 통계 카드 */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">활성 멘토</p>
                <p className="text-lg font-bold text-blue-600 dark:text-blue-400">24명</p>
              </div>
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <UserGroupIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">진행 중인 양육</p>
                <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">36건</p>
              </div>
              <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <BookOpenIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
            </div>
          </div>
        </div>

        {/* 주요 내용 */}
        <div className="lg:col-span-2">
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">양육 통계</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">전체 멘토 수</span>
                  <span className="font-semibold text-gray-800 dark:text-white">24명</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">양육 중인 가족</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">36가족</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">완료된 양육</span>
                  <span className="font-semibold text-green-600 dark:text-green-400">18가족</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-300">평균 완료 기간</span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">4.5개월</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'mentors' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">주요 멘토</h3>
              <div className="space-y-3">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center">
                      <StarIconSolid className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 dark:text-white">이목사 {item}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">5명 양육 중</p>
                    </div>
                    <div className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs">
                      베테랑
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'progress' && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">양육 단계별 현황</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">1단계: 기초 신앙</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">12건</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '33%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">2단계: 성숙한 신앙</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">18건</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">3단계: 사역자 훈련</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">6건</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{ width: '17%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 추가적인 세부 통계 컴포넌트 - 강화된 버전
export const DetailedStatsSection: React.FC = () => {
  const [stats, setStats] = useState({
    growthRate: 0,
    participationRate: 0,
    financialHealth: 0,
    satisfactionRate: 0
  });

  // 애니메이션 효과
  useEffect(() => {
    const timer = setTimeout(() => {
      setStats({
        growthRate: 12.5,
        participationRate: 87,
        financialHealth: 95,
        satisfactionRate: 92
      });
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const getStatColor = (value: number, type: 'growth' | 'participation' | 'health' | 'satisfaction') => {
    if (type === 'growth') {
      if (value >= 15) return 'from-indigo-500 to-purple-600';
      if (value >= 10) return 'from-blue-500 to-cyan-600';
      return 'from-teal-500 to-green-600';
    }
    if (type === 'participation') {
      if (value >= 90) return 'from-emerald-500 to-green-600';
      if (value >= 80) return 'from-green-500 to-teal-600';
      return 'from-yellow-500 to-orange-600';
    }
    if (type === 'health') {
      if (value >= 95) return 'from-emerald-500 to-green-600';
      if (value >= 85) return 'from-green-500 to-teal-600';
      return 'from-yellow-500 to-orange-600';
    }
    return 'from-purple-500 to-pink-600';
  };

  const getStatIcon = (type: string) => {
    switch (type) {
      case 'growth':
        return <HeartIcon className="h-6 w-6" />;
      case 'participation':
        return <UserPlusIcon className="h-6 w-6" />;
      case 'health':
        return <ChartBarIcon className="h-6 w-6" />;
      case 'satisfaction':
        return <StarIconSolid className="h-6 w-6" />;
      default:
        return <ChartBarIcon className="h-6 w-6" />;
    }
  };

  const getStatDescription = (type: string, value: number) => {
    switch (type) {
      case 'growth':
        return value >= 15 ? '뛰어난 성장세' : value >= 10 ? '꾸준한 성장세' : '성장 중';
      case 'participation':
        return value >= 90 ? '매우 높은 참여도' : value >= 80 ? '높은 참여도' : '개선 필요';
      case 'health':
        return value >= 95 ? '매우 건전함' : value >= 85 ? '건전함' : '관리 필요';
      case 'satisfaction':
        return value >= 90 ? '매우 만족함' : value >= 80 ? '만족함' : '개선 중';
      default:
        return '';
    }
  };

  const statItems = [
    {
      type: 'growth' as const,
      title: '교회 성장',
      value: stats.growthRate,
      unit: '%',
      description: '지난 3개월간 꾸준한 성장세를 보이고 있습니다'
    },
    {
      type: 'participation' as const,
      title: '새가족 참여도',
      value: stats.participationRate,
      unit: '%',
      description: '새가족들의 적극적인 사역 참여가 돋보입니다'
    },
    {
      type: 'health' as const,
      title: '재정 건전성',
      value: stats.financialHealth,
      unit: '%',
      description: '안정적인 재정 기반을 바탕으로 사역 확대 중'
    },
    {
      type: 'satisfaction' as const,
      title: '만족도 조사',
      value: stats.satisfactionRate,
      unit: '%',
      description: '회원들의 만족도가 높은 수준을 유지하고 있습니다'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {statItems.map((item, index) => (
        <div
          key={item.type}
          className={`bg-gradient-to-br ${getStatColor(item.value, item.type)} p-6 rounded-xl text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 relative overflow-hidden`}
        >
          {/* 배경 패턴 */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-white/20 transform rotate-45"></div>
          </div>
          
          <div className="flex items-center justify-between mb-4 relative z-10">
            <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
              {getStatIcon(item.type)}
            </div>
            <div className="text-right">
              <p className="text-lg font-bold animate-pulse">{item.value}{item.unit}</p>
              <p className="text-sm opacity-80">{getStatDescription(item.type, item.value)}</p>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2 relative z-10">{item.title}</h3>
          <p className="text-sm opacity-90 relative z-10">{item.description}</p>
          
          {/* 진행 표시기 */}
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
            <div
              className="h-full bg-white/60 transition-all duration-1000"
              style={{ width: `${Math.min(item.value, 100)}%` }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

// 실시간 활동 모니터링 컴포넌트
export const RealTimeActivityMonitor: React.FC = () => {
  const [activities, setActivities] = useState([
    { id: 1, type: 'member', user: '김교인', action: '새로운 가족 등록', time: '2분 전', status: 'success' },
    { id: 2, type: 'finance', user: '이집사', action: '주간 헌금 등록', time: '5분 전', status: 'success' },
    { id: 3, type: 'discipleship', user: '목사님', action: '양육 과정 완료', time: '8분 전', status: 'success' },
    { id: 4, type: 'visitation', user: '권사님', action: '방문 기록 작성', time: '12분 전', status: 'success' },
    { id: 5, type: 'event', user: '김전도사', action: '이벤트 생성', time: '15분 전', status: 'success' }
  ]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      default:
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <ClockIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">실시간 활동 모니터링</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">최근 활동 실시간 추적</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-500 dark:text-gray-400">실시간</span>
        </div>
      </div>

      <div className="space-y-3">
        {activities.map((activity) => (
          <div
            key={activity.id}
            className={`flex items-center space-x-3 p-3 rounded-lg border ${getStatusColor(activity.status)} transition-all duration-200 hover:shadow-sm`}
          >
            {getStatusIcon(activity.status)}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-white">
                <span className="font-semibold">{activity.user}</span> {activity.action}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{activity.time}</p>
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {activity.type}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 text-center">
        <button className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
          모든 활동 보기 →
        </button>
      </div>
    </div>
  );
};
